import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

// 🔹 Content Data (Using your Cloudinary images and descriptions)
const industries = [
  {
    num: "01",
    name: "Healthcare",
    link: "/industries/healthcare",
    img: "https://res.cloudinary.com/dpn3bkjba/image/upload/f_auto,q_auto:good,w_1200/v1770613556/Healthcare_o1eikj.png",
    desc: "Protecting patient data and critical medical infrastructure with HIPAA-compliant security solutions.",
  },
  {
    num: "02",
    name: "Mining & Manufacturing",
    link: "/industries/mining-manufacturing",
    img: "https://res.cloudinary.com/dpn3bkjba/image/upload/f_auto,q_auto:good,w_1200/v1770613383/Manufacturing_klt1zk.png",
    desc: "Securing OT/IT convergence and industrial control systems against targeted cyber threats.",
  },
  {
    num: "03",
    name: "Logistics",
    link: "/industries/logistics",
    img: "https://res.cloudinary.com/dpn3bkjba/image/upload/f_auto,q_auto:good,w_1200/v1770613385/Logistics_u0uuwm.png",
    desc: "End-to-end supply chain protection ensuring seamless operations and data integrity.",
  },
  {
    num: "04",
    name: "Technology",
    link: "/industries/technology",
    img: "https://res.cloudinary.com/dpn3bkjba/image/upload/f_auto,q_auto:good,w_1200/v1770613375/Technology_d5tld2.png",
    desc: "Advanced threat intelligence and zero-trust architecture for tech innovators.",
  },
  {
    num: "05",
    name: "Retail & Hospitality",
    link: "/industries/retail-hospitality",
    img: "https://res.cloudinary.com/dpn3bkjba/image/upload/f_auto,q_auto:good,w_1200/v1770613372/Retail_and_hospitality_k2hyoz.png",
    desc: "PCI-DSS compliant solutions protecting customer transactions and brand reputation.",
  },
  {
    num: "06",
    name: "Finance & Insurance",
    link: "/industries/finance-insurance",
    img: "https://res.cloudinary.com/dpn3bkjba/image/upload/f_auto,q_auto:good,w_1200/v1770613371/Finance_frtarj.png",
    desc: "Enterprise-grade security for banking, insurance, and investment firms.",
  },
];

// 🔹 Reusable Magnetic Button Component (Green Tone)
const MagneticButton = ({ children, className }) => {
  const handleMouseMove = (e) => {
    const btn = e.currentTarget;
    const rect = btn.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    btn.style.transform = `translate(${x * 0.2}px, ${y * 0.2}px)`;
  };

  const handleMouseLeave = (e) => {
    e.currentTarget.style.transform = "translate(0px, 0px)";
  };

  return (
    <button
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={`relative overflow-hidden transition-transform duration-300 ease-out before:absolute before:top-0 before:-left-full before:w-full before:h-full before:bg-gradient-to-r before:from-transparent before:via-[#00C08B]/20 before:to-transparent hover:before:left-full before:transition-[left] before:duration-500 ${className}`}
    >
      {children}
    </button>
  );
};

// 🔹 Animation Variants
const textReveal = {
  hidden: { y: "100%", opacity: 0 },
  visible: (custom) => ({
    y: 0,
    opacity: 1,
    transition: { delay: custom * 0.1, duration: 1.2, ease: [0.16, 1, 0.3, 1] },
  }),
};

const cardEntrance = {
  hidden: { y: 60, opacity: 0 },
  visible: (custom) => ({
    y: 0,
    opacity: 1,
    transition: { delay: custom * 0.1, duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  }),
};

const IndustriesPage = () => {
  // 3D Parallax Hover Effect for Cards
  const handleCardMouseMove = (e) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = (y - centerY) / 20;
    const rotateY = (centerX - x) / 20;

    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-8px) scale(1.02)`;
  };

  const handleCardMouseLeave = (e) => {
    e.currentTarget.style.transform =
      "perspective(1000px) rotateX(0deg) rotateY(0deg) translateY(0px) scale(1)";
  };

  return (
    // Premium Green Gradient Background
    <div className="bg-[radial-gradient(ellipse_at_top,_#0F2F2B_0%,_#05110f_50%,_#000000_100%)] min-h-screen text-white font-sans overflow-x-hidden relative">
      {/* Noise Texture Overlay */}
      <div
        className="fixed inset-0 pointer-events-none opacity-[0.03] z-50"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      ></div>

      {/* ================= HERO SECTION ================= */}
      <section className="relative pt-40 pb-24 px-6 lg:px-8 overflow-hidden">
        {/* Animated Green Background Orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ y: [-10, 10, -10] }}
            transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }}
            className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#2FAE8F]/[0.05] rounded-full blur-3xl"
          ></motion.div>
          <motion.div
            animate={{ y: [10, -10, 10] }}
            transition={{
              repeat: Infinity,
              duration: 6,
              ease: "easeInOut",
              delay: 3,
            }}
            className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-[#00C08B]/[0.03] rounded-full blur-3xl"
          ></motion.div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="max-w-4xl">
            {/* Subtitle */}
            <div className="mb-6 overflow-hidden">
              <p className="text-sm font-medium tracking-[0.3em] text-[#2FAE8F] uppercase">
                <span className="overflow-hidden inline-block">
                  <motion.span
                    custom={1}
                    initial="hidden"
                    animate="visible"
                    variants={textReveal}
                    className="inline-block"
                  >
                    Cybersecurity
                  </motion.span>
                </span>{" "}
                <span className="overflow-hidden inline-block">
                  <motion.span
                    custom={2}
                    initial="hidden"
                    animate="visible"
                    variants={textReveal}
                    className="inline-block"
                  >
                    Excellence
                  </motion.span>
                </span>
              </p>
            </div>

            {/* Title */}
            <h1 className="font-['Space_Grotesk',sans-serif] text-5xl md:text-7xl lg:text-8xl font-light leading-[0.95] tracking-tight mb-8">
              <div className="overflow-hidden">
                <motion.span
                  custom={1}
                  initial="hidden"
                  animate="visible"
                  variants={textReveal}
                  className="block text-white"
                >
                  Industries
                </motion.span>
              </div>
            </h1>

            {/* Description */}
            <div className="max-w-2xl overflow-hidden">
              <motion.p
                custom={3}
                initial="hidden"
                animate="visible"
                variants={textReveal}
                className="text-lg md:text-xl text-[#7FA9A2] font-light leading-relaxed"
              >
                At Synthera Security, we deliver tailored cybersecurity
                solutions for different industries. Our experienced team
                provides customized protection strategies to help organizations
                achieve their security goals and operate with confidence.
              </motion.p>
            </div>
          </div>

          {/* Line Expansion Animation */}
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: "100%" }}
            transition={{ duration: 2, ease: "easeOut" }}
            className="mt-16 h-px bg-gradient-to-r from-transparent via-[#2FAE8F]/50 to-transparent"
          ></motion.div>
        </div>
      </section>

      {/* ================= INDUSTRIES GRID ================= */}
      <section className="relative py-24 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {industries.map((item, index) => (
              <motion.div
                key={index}
                custom={index + 1}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
                variants={cardEntrance}
              >
                <Link
                  to={item.link}
                  onMouseMove={handleCardMouseMove}
                  onMouseLeave={handleCardMouseLeave}
                  className="group relative h-[500px] rounded-2xl overflow-hidden block shadow-[0_0_80px_rgba(47,174,143,0.03)] transition-all duration-500 ease-out z-10
                             before:absolute before:inset-[-1px] before:bg-gradient-to-br before:from-[#2FAE8F]/20 before:to-transparent before:rounded-2xl before:-z-10 before:opacity-0 hover:before:opacity-100 before:transition-opacity before:duration-400"
                >
                  {/* Background Image */}
                  <div className="absolute inset-0">
                    <img
                      src={item.img}
                      alt={item.name}
                      className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-transform duration-700 group-hover:scale-110"
                    />
                  </div>

                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 flex flex-col justify-end p-8 bg-gradient-to-t from-[#05110f]/95 via-[#0A201D]/60 to-transparent group-hover:from-[#05110f]/100 group-hover:via-[#0F2F2B]/80 group-hover:to-transparent transition-all duration-400">
                    {/* Inner Container that translates up on hover */}
                    <div className="transform transition-transform duration-500 group-hover:translate-y-0 translate-y-6">
                      {/* Number */}
                      <span className="text-xs font-medium tracking-widest text-[#2FAE8F] uppercase mb-2 block">
                        {item.num}
                      </span>

                      {/* Title */}
                      <h3 className="font-['Space_Grotesk',sans-serif] text-3xl font-medium mb-3 text-white">
                        {item.name}
                      </h3>

                      {/* EXACT PARAGRAPH CLASS FROM PROMPT */}
                      <p className="text-gray-300 text-sm leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100 max-w-xs">
                        {item.desc}
                      </p>

                      {/* Revealing Link */}
                      <div className="mt-6 flex items-center text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-200 text-[#00C08B]">
                        <span>Explore Solutions</span>
                        <svg
                          className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M17 8l4 4m0 0l-4 4m4-4H3"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= CTA SECTION ================= */}
      <section className="relative py-32 px-6 lg:px-8 border-t border-[#2FAE8F]/10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-['Space_Grotesk',sans-serif] text-4xl md:text-5xl font-light mb-6 bg-gradient-to-br from-white to-[#7FA9A2] bg-clip-text text-transparent">
            Ready to secure your industry?
          </h2>
          <p className="text-[#7FA9A2] text-lg mb-10 max-w-2xl mx-auto font-light">
            Partner with Synthera Security for comprehensive protection tailored
            to your specific sector requirements.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/Contact" className="inline-block">
              <MagneticButton className="px-8 py-4 bg-[#00C08B] text-white font-semibold rounded-full text-sm tracking-wide w-full sm:w-auto shadow-lg shadow-[#00C08B]/20">
                Schedule Consultation
              </MagneticButton>
            </Link>
            <Link
              to="/services"
              className="px-8 py-4 border border-[#2FAE8F]/30 text-white font-medium rounded-full hover:bg-[#2FAE8F]/10 transition-all duration-300 text-sm tracking-wide text-center"
            >
              View All Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IndustriesPage;
